<template>
  <fragment>
    <span v-if="isTitleVisible && title" class="font_Montserrat font_size_1000rem v3_color_black_knight1">
      {{ title }}
    </span>
    <span v-else class="font_Montserrat font_size_1000rem">&nbsp;</span>
    <div class=" survey-results-chart-column" :class="{'border_1 border_grey' : isBorderVisible}">
      <highcharts :options="chartOptions" />
    </div>
    <div v-if="isQuestionTypeSlider" class="text-center survey-results__legend">
      {{ legend }}
    </div>

    <template
      v-if="isStatsVisible && !isNoStudyResults && !isJobQuestionResults"
    >
      <div v-if="isQuestionTypeSlider" class="mt-3">
        <!-- My Value-->
        <b-row
          v-if="!isMssResults"
          class="mt-4 font_Montserrat font_size_875rem v3_color_black_knight"
        >
          <b-col cols="6" class="">
            <span>My Value:</span>
            <span class="ml-4 v3_color_teal">
              {{ prefix }}
              <span v-if="stats.my_value">
                {{ stats.my_value | formatNumber }}
              </span>
              <span v-else>0</span>
              {{ suffix }}
            </span>
          </b-col>
        </b-row>

        <template v-if="isWithReport">
          <b-row>
            <b-col cols="12" sm="12" class="mb-2">
              <table class="stats-table w-100">
                <thead>
                <tr>
                  <th>N</th>
                  <th>Min</th>
                  <th>Max</th>
                  <th>
                    <em>
                      P
                      <sub>25</sub>
                    </em>
                  </th>
                  <th>Median</th>
                  <th>
                    <em>
                      P
                      <sub>75</sub>
                    </em>
                  </th>
                </tr>
                </thead>
                <tbody>
                <tr>
                  <td>{{ stats.count | formatNumber }}</td>
                  <td>
                    {{ statsPrefix }}{{ stats.min | formatNumber
                    }}{{ statsSuffix }}
                  </td>
                  <td>
                    {{ statsPrefix }}{{ stats.max | formatNumber
                    }}{{ statsSuffix }}
                  </td>
                  <td>
                    {{ statsPrefix }}
                    {{ stats.percentile_25 | formatNumber }}
                    {{ statsSuffix }}
                  </td>
                  <td>
                    {{ statsPrefix }}
                    {{ stats.median | formatNumber }}
                    {{ statsSuffix }}
                  </td>
                  <td>
                    {{ statsPrefix }}
                    {{ stats.percentile_75 | formatNumber }}
                    {{ statsSuffix }}
                  </td>
                </tr>
                </tbody>
              </table>
            </b-col>
          </b-row>
        </template>
        <template v-else>
          <b-row>
            <b-col cols="12" sm="12" lg="6" class="mb-2">
              <table class="stats-table w-100">
                <thead>
                <tr>
                  <th>N</th>
                  <th>Min</th>
                  <th>Max</th>
                </tr>
                </thead>
                <tbody>
                <tr>
                  <td>{{ stats.count | formatNumber }}</td>
                  <td>
                    {{ statsPrefix }}{{ stats.min | formatNumber
                    }}{{ statsSuffix }}
                  </td>
                  <td>
                    {{ statsPrefix }}{{ stats.max | formatNumber
                    }}{{ statsSuffix }}
                  </td>
                </tr>
                </tbody>
              </table>
            </b-col>
            <b-col cols="12" sm="12" lg="6">
              <table class="stats-table w-100">
                <thead>
                <tr>
                  <th>
                    <em>
                      P
                      <sub>25</sub>
                    </em>
                  </th>
                  <th>Median</th>
                  <th>
                    <em>
                      P
                      <sub>75</sub>
                    </em>
                  </th>
                </tr>
                </thead>
                <tbody>
                <tr>
                  <td>
                    {{ statsPrefix }}
                    {{ stats.percentile_25 | formatNumber }}
                    {{ statsSuffix }}
                  </td>
                  <td>
                    {{ statsPrefix }}
                    {{ stats.median | formatNumber }}
                    {{ statsSuffix }}
                  </td>
                  <td>
                    {{ statsPrefix }}
                    {{ stats.percentile_75 | formatNumber }}
                    {{ statsSuffix }}
                  </td>
                </tr>
                </tbody>
              </table>
            </b-col>
          </b-row>
        </template>

        <div style="clear: both;"></div>
      </div>
    </template>
    <template v-else-if="(isNoStudyResults || isJobQuestionResults) && stats">
      <div
        class="my-3 text-center  v2_color_med_gray v2_font_size_20"
      >
        <span>Median =</span>
        <span>
          {{ statsPrefix }}{{ stats.median | formatNumber }}{{ statsSuffix }}
        </span>
      </div>
    </template>
    <div
      v-if="responsesCount"
      class="text-right survey-results__responses-count mt-1"
    >
      <span>N = {{ responsesCount | formatNumber }}</span>
      <span v-if="unknownResponsesCount">,<span class="pl-1">Unknown = {{ unknownResponsesCount | formatNumber }}</span></span>
    </div>
  </fragment>
</template>

<script>
import { Chart } from 'highcharts-vue';
import FormatNumberFilterMixin from '@/mixins/FormatNumberFilterMixin';
import {
  getCharHeightForChartTypeAndWidth,
  getChartLegend,
  getMaxValueFromResults,
  getPercentageFormatter,
  getPercentageFormatterForBar,
} from '@/helpers/chartsHelper';
import ChartsFormatterMixin from '@/mixins/ChartsFormatterMixin';
import { CHART_ORIENTATIONS, QUESTION_TYPES } from '@/store';
import Utils from '@/providers/Utils';

export default {
  name: 'SurveyResultsChartBar',
  mixins: [FormatNumberFilterMixin, ChartsFormatterMixin],
  components: {
    highcharts: Chart,
  },
  props: {
    title: {
      type: String,
      default: '',
    },
    results: Array,
    format: {
      type: String, // 'dashboard' / 'results' / 'filters' / 'no-study-completed'
      default: 'results',
    },
    responsesCount: [Number, String],
    unknownResponsesCount: {
      type: [Number, String],
      default: 0
    },
    questionType: String,
    responses: Array,
    stats: Object,
    question: Object,
    isPercentage: {
      type: Boolean,
      default: false,
    },
    orientation: {
      type: String,
      default: CHART_ORIENTATIONS.VERTICAL, // 'horizontal', 'vertical'
    },
    yTitle: {
      type: String,
      default: 'Count',
    },
    allowDecimals: {
      type: Boolean,
      default: false,
    },
    variable: {
      type: Object,
      default: function () {
        return {};
      },
    },
    isStatsVisible: {
      type: Boolean,
      default: true,
    },
    isTitleVisible: {
      type: Boolean,
      default: true
    },
    isBorderVisible: {
      type: Boolean,
      default: true
    },
    isWithReport: {
      type: Boolean,
      default: false
    },
  },
  data: function () {
    const isChartOrientationHorizontal =
      this.orientation === CHART_ORIENTATIONS.HORIZONTAL;
    const chartOptions = {
      chart: {
        backgroundColor: 'transparent',
        events: {
          render: () => this.$emit('chart-rendered'),
        },
        type: isChartOrientationHorizontal ? 'bar' : 'column',
        height: getCharHeightForChartTypeAndWidth(
          this.format,
          this.windowWidth
        ),
        // width: 700
      },
      title: undefined,
      legend: { enabled: false },
      credits: { enabled: false },
      xAxis: {
        allowDecimals: this.allowDecimals,
        categories: this.results.map((r) => {
          return this.getDataPointName(r);
        }),
        crosshair: true,
        reversed: false,
        visible: true,
        //visible: this.format !== 'no-study-completed',
      },
      yAxis: {
        allowDecimals: this.allowDecimals,
        min: 0,
        title: {
          text: this.yTitle,
        },
      },

      plotOptions: {
        column: {
          allowPointSelect: true,
          cursor: 'pointer',
          dataLabels: {
            enabled: false,
            formatter: this.getDataLabelsFormatter(),
          },
        },
        series: {
          color: '#5AC3D8',
        },
      },
      series: [
        {
          data: this.results.map((r) => {
            return {
              name: this.getDataPointName(r),
              y: +r.value_count,
            };
          }),
          tooltip: {
            headerFormat: '',
            pointFormatter: this.isPercentage
              ? getPercentageFormatterForBar(this.responsesCount, 'y')
              : function () {
                  return `<strong>${this.name}</strong> - ${this.y}`;
                },
          },
        },
      ],
    };
    if (this.isPercentage) {
      let yAxisValues=[];
      this.results.map((r) => {
        yAxisValues.push(r.value_count);
      });
      let maxValue = (yAxisValues.length > 0) ? Math.max(...yAxisValues) : 0;

      chartOptions.yAxis.labels = {
        formatter: getPercentageFormatterForBar(this.responsesCount),
      };
      ///// comment previous dev code /////
      //chartOptions.yAxis.max = this.responsesCount;
      ////// new line of code ///////
      chartOptions.yAxis.max = (maxValue > 0) ? (maxValue + (maxValue*0.05)) : this.responsesCount;
      chartOptions.yAxis.tickAmount = 5;
      chartOptions.yAxis.tickInterval = chartOptions.yAxis.max / 4;
    }
    return {
      chartOptions: chartOptions,
    };
  },
  created: function () {
    this.updateChartHeight();
  },
  computed: {
    isMssResults: function () {
      return this.format === 'admin-survey-results';
    },
    isNoStudyResults: function () {
      return this.format === 'no-study-completed';
    },
    isJobQuestionResults: function () {
      return this.format === 'job-question';
    },
    prefix: function () {
      if (this.question.firstVariable.prefix) {
        return this.question.firstVariable.prefix;
      } else {
        return '';
      }
    },
    statsPrefix: function () {
      // only show this if it's short
      return this.prefix.length <= 2 ? this.prefix : '';
    },
    suffix: function () {
      if (this.question.firstVariable.suffix) {
        if (
          parseInt(this.stats.my_value) === 1 &&
          this.question.firstVariable.singular_suffix
        ) {
          return this.question.firstVariable.singular_suffix;
        } else {
          return this.question.firstVariable.suffix;
        }
      } else {
        return '';
      }
    },
    statsSuffix: function () {
      // only show this if it's short
      return this.suffix && this.suffix.length <= 2 ? this.suffix : '';
    },
    isQuestionTypeSlider: function () {
      return this.questionType === QUESTION_TYPES.SLIDER;
    },
    legend: function () {
      return getChartLegend(this.variable);
    },
  },
  methods: {
    updateChartHeight: function () {
      this.chartOptions.chart.height = getCharHeightForChartTypeAndWidth(
        this.format,
        this.windowWidth
      );
    },
    numberSuffix: function (d) {
      return Utils.numberSuffix(d);
    },
  },
  watch: {
    responses: function () {
      // refresh data series
      if (
        this.chartOptions &&
        this.chartOptions.series &&
        this.chartOptions.series[1]
      ) {
        this.chartOptions.series[1].data = this.responses;
      }
    },
    windowWidth: function () {
      this.updateChartHeight();
    },
    results: function () {
      // refresh data series
      this.chartOptions.series[0].data = this.results.map((r) => {
        return {
          name: this.getDataPointName(r),
          y: +r.value_count,
        };
      });
      if (this.isPercentage) {
        this.chartOptions.yAxis.labels = {
          formatter: getPercentageFormatter(this.results),
        };
        this.chartOptions.yAxis.labels = {
          formatter: getPercentageFormatter(this.results),
        };
        const max = getMaxValueFromResults(this.results);
        this.chartOptions.yAxis.max = max;
        this.chartOptions.yAxis.tickAmount = 5;
        this.chartOptions.yAxis.tickInterval = max / 4;
      }
    },
  },
};
</script>

<style lang="scss"></style>
